/** @jsx jsx */

/** eslint jsx-a11y/media-has-caption: 0 */
// videojs adds track tag in via js (flagged video element)
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';
import { isEqual } from 'lodash';
import videojs from 'video.js';
import type { Player, PlayerOptions } from 'video.js';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import supportsNativeTextTracks from 'pages/open-course/video/util/supportsNativeTextTracks';

import { InlineNotification, Typography2 } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type { VideoSrcItem } from 'bundles/video-logged-out-page/types/vlpSharedTypes';

import _t from 'i18n!nls/video-logged-out-page';

// eslint-disable-next-line no-restricted-syntax
import 'css!bundles/videojs/css/videojsResolutionSwitcher';
import 'css!video.js/dist/video-js';

export const loadVideoJsResolutionSwitcher = async () =>
  /* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
  import('bundles/videojs/plugins/videojsResolutionSwitcher');

export const loadClippedVideoSeekBar = async () =>
  /* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
  import('bundles/videojs/plugins/video-trim/clippedVideoPlayerPluginsSeekbar');

const styles = {
  videoJSPlayerContainer: css`
    position: relative;
    padding-top: 56.25%; /* displays at 16:9 ratio */
    top: 0;
    left: 0;
    color: #fff;
    transition: all 250ms ease-out;

    /* Hide volume mouse hover display (maintains existing pre-upgrade functionality) */
    .vjs-volume-control {
      .vjs-volume-bar {
        .vjs-mouse-display {
          display: none;
        }
      }
    }

    .customComponent-ClippedProgressControl .vjs-load-progress {
      overflow: hidden;
    }
  `,
  videoJSPlayer: css`
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    display: block;
    background-color: #1a1b1c;
    padding: 0;
    user-select: none;
  `,
};

const defaultVjsOptions = {
  preload: 'auto',
  autoplay: false,
  controls: true,
  html5: {
    nativeTextTracks: supportsNativeTextTracks(),
  },
  controlBar: {
    children: [
      'playToggle',
      'volumePanel',
      {
        name: 'clippedProgressControl',
        children: [
          {
            name: 'clippedSeekBar',
            children: ['loadProgressBar', 'clippedPlayProgressBar', 'mouseTimeDisplay'],
            barName: 'clippedPlayProgressBar',
          },
        ],
      },
      'subtitlesButton',
      'videoJsResolutionSwitcher',
      'fullscreenToggle',
    ],
  },
};

export type Props = {
  src: VideoSrcItem;
  type: string;
  poster: string;
  res: number;
  srcArray: Array<VideoSrcItem>;
  vjsOptions?: PlayerOptions;
  onReady?: () => void;
  getPlayer?: (element: Player) => void;
  eventListeners?: {
    playing?: () => void;
    ended?: () => void;
    seeking?: () => void;
    pause?: () => void;
    error?: () => void;
    loadedmetadata?: () => void;
    timeupdate?: () => void;
    durationchange?: () => void;
  };
  videoHtmlAttributes?: {};
  vjsSkin?: string;
  vjsBigPlayCentered?: boolean;
  startWithControlBar?: boolean;
  children?: React.ReactNode;
  defaultQuality?: 'high';
  videoRef?: (r: Element | null) => void;
};

export class VideoJSPlayer extends React.Component<Props> {
  declare _player: Player | null;

  declare _videoJsResolutionSwitcher: {} | null;

  declare _videoJsClippedSeekBar: {} | null;

  declare videoRef: HTMLVideoElement | null;

  static defaultProps = {
    vjsOptions: defaultVjsOptions,
    vjsSkin: 'vjs-default-skin',
    vjsBigPlayCentered: true,
    startWithControlBar: true,
  };

  constructor(props: Props) {
    super(props);

    this._player = null;
    this._videoJsResolutionSwitcher = null;
    this._videoJsClippedSeekBar = null;
    this.videoRef = null;
  }

  componentDidMount() {
    if (!this.useMultiResolution()) {
      this.mountVideoPlayer();
    } else {
      Promise.all([loadVideoJsResolutionSwitcher(), loadClippedVideoSeekBar()]).then(
        ([videoJsResolutionSwitcher, clippedSeekBar]) => {
          this._videoJsResolutionSwitcher = videoJsResolutionSwitcher.default;
          this._videoJsClippedSeekBar = clippedSeekBar.default;
          this.mountVideoPlayer();
        }
      );
    }
  }

  componentWillReceiveProps({ src, srcArray, type }: Props) {
    const { src: propsSrc, srcArray: propsSrcArray } = this.props;
    // Check if srcArray in props and incoming srcArray are different or the src is different and the srcArray is empty since it's either/or with src/srcArray
    // being extra clear here because this would run when the resolution changed, but it was the same video
    if ((src !== propsSrc && !(srcArray && srcArray.length)) || !isEqual(srcArray, propsSrcArray)) {
      this.setVideoPlayerSrc(src, srcArray, type);
    }
  }

  shouldComponentUpdate({ src, srcArray }: Props) {
    const { src: propsSrc, srcArray: curPropsSrcArray } = this.props;
    // Only update if the player src or srcArray changes
    if (src !== propsSrc || !isEqual(srcArray, curPropsSrcArray)) {
      return true;
    }
    return false;
  }

  componentWillUnmount() {
    this.unmountVideoPlayer();
  }

  handleVideoPlayerReady = () => {
    const { getPlayer, startWithControlBar, onReady, type, src, srcArray } = this.props;

    if (!this._player) return;

    if (this.useMultiResolution() && this._videoJsResolutionSwitcher) {
      // @ts-expect-error
      this._player.updateSrc(srcArray);
    } else {
      this._player.src({
        src,
        type,
      });
    }

    if (startWithControlBar) {
      // @ts-expect-error TSMIGRATION
      this._player.bigPlayButton.hide();
      // @ts-expect-error TSMIGRATION
      this._player.controlBar.show();
      this._player.userActive(true);
    }

    if (getPlayer) {
      getPlayer(this._player);
    }

    if (onReady) {
      onReady();
    }
  };

  getPlayerOptions = () => {
    const { vjsOptions, srcArray, defaultQuality, res } = this.props;

    let playerOptions = vjsOptions || {};
    if (this.useMultiResolution() && this._videoJsResolutionSwitcher) {
      const firstSrcItem = srcArray && srcArray[0];
      playerOptions = {
        ...playerOptions,
        plugins: {
          // @ts-expect-error TSMIGRATION
          ...playerOptions.plugins,
          videoJsResolutionSwitcher: {
            default: defaultQuality ?? res ?? (firstSrcItem && firstSrcItem.label),
            dynamicLabel: true,
          },
        },
      };
    }
    return { ...defaultVjsOptions, ...playerOptions };
  };

  useMultiResolution = () => {
    const { srcArray } = this.props;
    return Array.isArray(srcArray) && srcArray.length > 0;
  };

  mountVideoPlayer = () => {
    const { eventListeners } = this.props;
    const vjsOptions = this.getPlayerOptions();
    // @ts-expect-error TSMIGRATION
    this._player = videojs(this.videoRef, vjsOptions);

    if (this._player) {
      this._player.ready(this.handleVideoPlayerReady);
    }

    if (eventListeners) {
      Object.keys(eventListeners).forEach((key) => {
        if (this._player) {
          // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          this._player.on(key, eventListeners[key]);
        }
      });
    }
  };

  unmountVideoPlayer = () => {
    if (this._player) {
      this._player.dispose();
    }
  };

  setVideoPlayerSrc = (src: VideoSrcItem, srcArray: Array<VideoSrcItem>, type: string) => {
    if (!this._player) {
      return;
    }
    if (!this.useMultiResolution() || !this._videoJsResolutionSwitcher) {
      this._player.src({
        src,
        type,
      });
    } else {
      // @ts-expect-error TSMIGRATION
      this._player.updateSrc(srcArray);
      this._player.ready(this.handleVideoPlayerReady);
    }
  };

  render() {
    const { children, videoHtmlAttributes, vjsSkin, vjsBigPlayCentered } = this.props;
    const videoPlayerClasses = classNames(
      'video-js',
      {
        'vjs-big-play-centered': vjsBigPlayCentered,
      },
      vjsSkin
    );

    return (
      <div css={[styles.videoJSPlayerContainer]}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          {...videoHtmlAttributes}
          className={videoPlayerClasses}
          css={styles.videoJSPlayer}
          ref={(r) => {
            this.videoRef = r;
            this.props.videoRef?.(r);
          }}
          data-e2e="vlp-video-player"
        >
          {children || (
            <Typography2 component="p" className="vjs-no-js">
              <FormattedHTMLMessage
                message={_t(
                  `
                  To view this video please enable JavaScript, and consider upgrading to a web browser that
                  <a href="http://videojs.com/html5-video-support/" target="_blank" rel="noopener noreferrer">
                    supports HTML5 video
                  </a>
                `
                )}
              />
            </Typography2>
          )}
        </video>
      </div>
    );
  }
}

const ErrorBoundVideoJSPlayer = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="VideoJSPlayer"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t(
          'An error was encountered while playing this video, and our development team has been alerted. Please try again later.'
        )}
      </InlineNotification>
    }
  >
    <VideoJSPlayer {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundVideoJSPlayer;
